import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BORDEAUX = "#5a2b2b";
const CHAMPAGNE = "#f0e6cf";
const GOLD = "#e3c37a";

export const VajacialTestimonials = ({ items = [] }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center", duration: 30 });
  const [selected, setSelected] = useState(0);
  const autoplayRef = useRef(null);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
  }, []);

  const startAutoplay = useCallback(() => {
    if (!emblaApi) return;
    stopAutoplay();
    autoplayRef.current = setInterval(() => emblaApi.scrollNext(), 9000);
  }, [emblaApi, stopAutoplay]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    startAutoplay();
  }, [emblaApi, startAutoplay]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    startAutoplay();
  }, [emblaApi, startAutoplay]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("pointerDown", stopAutoplay);
    emblaApi.on("pointerUp", startAutoplay);
    startAutoplay();
    return () => {
      stopAutoplay();
      emblaApi.off("select", onSelect);
      emblaApi.off("pointerDown", stopAutoplay);
      emblaApi.off("pointerUp", startAutoplay);
    };
  }, [emblaApi, startAutoplay, stopAutoplay]);

  if (!items.length) return null;

  return (
    <div
      data-testid="vajacial-testimonials"
      onMouseEnter={stopAutoplay}
      onMouseLeave={startAutoplay}
      className="rounded-xl px-6 py-8 md:px-12 md:py-9"
      style={{ backgroundColor: BORDEAUX }}
    >
      <p className="gold-champagne text-[11px] tracking-[0.32em] uppercase mb-5 text-center">
        Leurs expériences
      </p>

      <div className="max-w-lg mx-auto">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {items.map((t, i) => (
              <div
                key={i}
                data-testid={`vajacial-testimonial-${i}`}
                className="flex-[0_0_100%] min-w-0 px-2 text-center"
              >
                <blockquote>
                  <p
                    className="text-sm md:text-[15px] leading-relaxed italic"
                    style={{ color: CHAMPAGNE }}
                  >
                    {t.text}
                  </p>
                </blockquote>
                <p className="gold-champagne mt-4 text-[11px] tracking-[0.22em] uppercase">
                  {t.name}
                </p>
              </div>
            ))}
          </div>
        </div>

        {items.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-5">
            <button
              type="button"
              aria-label="Témoignage précédent"
              data-testid="vajacial-testimonial-prev"
              onClick={scrollPrev}
              className="transition-opacity hover:opacity-70"
              style={{ color: GOLD }}
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>

            <div className="flex items-center gap-2">
              {items.map((_, i) => (
                <span
                  key={i}
                  data-testid={`vajacial-testimonial-dot-${i}`}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: i === selected ? 20 : 6,
                    backgroundColor: i === selected ? GOLD : "rgba(240,230,207,0.35)",
                  }}
                />
              ))}
            </div>

            <button
              type="button"
              aria-label="Témoignage suivant"
              data-testid="vajacial-testimonial-next"
              onClick={scrollNext}
              className="transition-opacity hover:opacity-70"
              style={{ color: GOLD }}
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
